import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4 text-left" }
const _hoisted_2 = { class: "flex items-center justify-between mb-3.5" }
const _hoisted_3 = { class: "text-base font-bold text-primary30" }
const _hoisted_4 = { class: "text-sm text-[#52526f]" }
const _hoisted_5 = ["cx", "cy", "stroke"]
const _hoisted_6 = ["cx", "cy", "stroke", "stroke-dashoffset"]
const _hoisted_7 = { class: "flex-1 flex flex-col gap-0.5 min-w-0 overflow-hidden" }
const _hoisted_8 = { class: "text-sm font-semibold text-[#222]" }
const _hoisted_9 = { class: "text-xs text-[#52526f]" }
const _hoisted_10 = {
  key: 0,
  class: "text-xs text-[#52526f]"
}
const _hoisted_11 = {
  key: 1,
  class: "text-xs font-medium text-[#a15c07]",
  "data-testid": "candidate-expiration"
}
const _hoisted_12 = {
  key: 2,
  class: "text-xs text-[#999] italic"
}
const _hoisted_13 = { class: "shrink-0" }
const _hoisted_14 = /*#__PURE__*/_createElementVNode("div", { class: "h-3.5" }, null, -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("div", { class: "h-px bg-[#e0e0e0]" }, null, -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("div", { class: "h-3.5" }, null, -1)
const _hoisted_17 = {
  key: 0,
  class: "flex items-center justify-center py-[32px] text-sm text-[#7f7f8e]",
  "data-testid": "candidates-list-empty"
}

import { computed } from 'vue'
import { differenceInDays, differenceInHours, format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { InviteResponseV2 } from '@/Network/Types/Responses/HandoverInviteResponses'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import { getDoctorInitials } from '@/utils/string'
import { InviteStatus, InviteOrigin } from '@/Network/Types/Requests/HandoverInviteRequests'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'

const AVATAR_SIZE = 40
const STROKE_WIDTH = 4

export default /*#__PURE__*/_defineComponent({
  __name: 'CandidatesList',
  props: {
    invites: {},
    columns: { default: 1 }
  },
  setup(__props: any) {

const props = __props;



const RADIUS = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const SVG_SIZE = RADIUS * 2 + STROKE_WIDTH
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const INNER_STYLE = {
  width: AVATAR_SIZE + 'px',
  height: AVATAR_SIZE + 'px',
  top: STROKE_WIDTH + 'px',
  left: STROKE_WIDTH + 'px'
}

function getClampedScore(score?: number | null): number {
  if (score == null) return 0
  return Math.min(100, Math.max(0, score))
}

function getArcOffset(score?: number | null): number {
  return CIRCUMFERENCE * (1 - getClampedScore(score) / 100)
}

function getArcColor(score?: number | null): string {
  if (score == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(score).color
}

const directInvites = computed(() => props.invites.filter((i) => i.origin === InviteOrigin.DIRECT))

const showcaseInvites = computed(() =>
  props.invites.filter((i) => i.origin === InviteOrigin.SHOWCASE)
)

// Grupos com convites, na ordem de exibição; grupo vazio não aparece.
const groups = computed(() =>
  [
    {
      title: 'Convidados diretos',
      items: directInvites.value,
      countLabel: `${directInvites.value.length} ${
        directInvites.value.length === 1 ? 'convidado' : 'convidados'
      }`
    },
    {
      title: 'Candidatos da vitrine',
      items: showcaseInvites.value,
      countLabel: `${showcaseInvites.value.length} ${
        showcaseInvites.value.length === 1 ? 'candidato' : 'candidatos'
      }`
    }
  ].filter((group) => group.items.length)
)

const acceptedDirectQueue = computed(() =>
  directInvites.value
    .filter((i) => i.status === InviteStatus.ACCEPTED)
    .sort((a, b) => {
      if (!a.acceptedAt || !b.acceptedAt) return 0
      return new Date(a.acceptedAt).getTime() - new Date(b.acceptedAt).getTime()
    })
)

function getQueuePosition(invite: InviteResponseV2): number {
  return acceptedDirectQueue.value.findIndex((i) => i.id === invite.id) + 1
}

const AVATAR_COLORS = [
  '#4778c7',
  '#804db2',
  '#995933',
  '#2e7d32',
  '#c62828',
  '#00838f',
  '#ad1457',
  '#4527a0',
  '#ef6c00',
  '#1565c0'
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function formatAcceptedAt(dateStr: string): string {
  try {
    const date = parseISO(dateStr)
    return format(date, "EEE dd/MM 'às' HH'h'mm", { locale: ptBR })
  } catch {
    return dateStr
  }
}

/**
 * Prazo restante do convite, só para quem ainda pode responder. Um convite já
 * respondido ou já vencido tem o próprio status contando a história — repetir o
 * prazo ali só competiria com ele.
 */
function getExpirationLabel(invite: InviteResponseV2): string | null {
  if (invite.status !== InviteStatus.PENDING || !invite.expiresAt) return null

  try {
    const expiresAt = parseISO(invite.expiresAt)
    const hours = differenceInHours(expiresAt, new Date())
    if (hours < 0) return null
    if (hours < 24) return `Expira em ${hours} ${hours === 1 ? 'hora' : 'horas'}`

    const days = differenceInDays(expiresAt, new Date())
    return `Expira em ${days} ${days === 1 ? 'dia' : 'dias'}`
  } catch {
    return null
  }
}

function getStatusLabel(invite: InviteResponseV2): string {
  if (invite.status === InviteStatus.ACCEPTED) {
    const pos = getQueuePosition(invite)
    return pos > 0 ? `${pos}º na fila` : 'Aceito'
  }
  if (invite.status === InviteStatus.PENDING) return 'Aguardando'
  if (invite.status === InviteStatus.DECLINED) return 'Recusou'
  if (invite.status === InviteStatus.EXPIRED) return 'Expirado'
  if (invite.status === InviteStatus.CANCELLED) return 'Cancelado'
  return invite.status
}

function getStatusBadgeClasses(invite: InviteResponseV2): string {
  if (invite.status === InviteStatus.ACCEPTED) {
    return 'bg-primary5 text-[#235e6c]'
  }
  return 'bg-[#e2e2e9] text-[#999]'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
      return (_openBlock(), _createElementBlock("div", {
        key: group.title,
        class: "flex flex-col"
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(group.title), 1),
          _createElementVNode("span", _hoisted_4, _toDisplayString(group.countLabel), 1)
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(_ctx.columns === 2 ? 'grid grid-cols-1 lg:grid-cols-2 gap-x-10' : 'contents')
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (invite, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: invite.id
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["flex items-start gap-3", { 'py-[14px] border-b border-[#e0e0e0]': _ctx.columns === 2 }])
              }, [
                _createElementVNode("div", {
                  class: "relative shrink-0",
                  style: _normalizeStyle({ width: SVG_SIZE + 'px', height: SVG_SIZE + 'px' })
                }, [
                  (_openBlock(), _createElementBlock("svg", {
                    width: SVG_SIZE,
                    height: SVG_SIZE,
                    class: "absolute top-0 left-0 -rotate-90",
                    "aria-hidden": "true"
                  }, [
                    _createElementVNode("circle", {
                      cx: SVG_SIZE / 2,
                      cy: SVG_SIZE / 2,
                      r: RADIUS,
                      stroke: _unref(NEUTRAL_RING_COLOR),
                      "stroke-width": STROKE_WIDTH,
                      fill: "none"
                    }, null, 8, _hoisted_5),
                    (getClampedScore(invite.doctor.npsScore) > 0)
                      ? (_openBlock(), _createElementBlock("circle", {
                          key: 0,
                          cx: SVG_SIZE / 2,
                          cy: SVG_SIZE / 2,
                          r: RADIUS,
                          stroke: getArcColor(invite.doctor.npsScore),
                          "stroke-width": STROKE_WIDTH,
                          fill: "none",
                          "stroke-dasharray": CIRCUMFERENCE,
                          "stroke-dashoffset": getArcOffset(invite.doctor.npsScore),
                          "stroke-linecap": "round"
                        }, null, 8, _hoisted_6))
                      : _createCommentVNode("", true)
                  ])),
                  _createElementVNode("div", {
                    class: "absolute rounded-full flex items-center justify-center text-sm font-semibold text-white",
                    style: _normalizeStyle({ ...INNER_STYLE, backgroundColor: getAvatarColor(invite.doctor.name) })
                  }, _toDisplayString(_unref(getDoctorInitials)(invite.doctor.name)), 5)
                ], 4),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(getFirstAndSecondName)(invite.doctor.name)), 1),
                  _createElementVNode("span", _hoisted_9, "CRM " + _toDisplayString(invite.doctor.crm), 1),
                  (invite.acceptedAt)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, " Aceitou em " + _toDisplayString(formatAcceptedAt(invite.acceptedAt)), 1))
                    : _createCommentVNode("", true),
                  (getExpirationLabel(invite))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(getExpirationLabel(invite)), 1))
                    : _createCommentVNode("", true),
                  (invite.declineReason)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, " Motivo: " + _toDisplayString(invite.declineReason), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["inline-block px-2 py-[3px] rounded-[10px] text-[11px] font-medium whitespace-nowrap", getStatusBadgeClasses(invite)])
                  }, _toDisplayString(getStatusLabel(invite)), 3)
                ])
              ], 2),
              (_ctx.columns === 1 && index < group.items.length - 1)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _hoisted_14,
                    _hoisted_15,
                    _hoisted_16
                  ], 64))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ], 2)
      ]))
    }), 128)),
    (!groups.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_17, " Nenhum convidado encontrado para esta troca. "))
      : _createCommentVNode("", true)
  ]))
}
}

})